import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  Camera, 
  Image, 
  X, 
  FileImage,
  CheckCircle,
  AlertCircle,
  Lightbulb,
  Sun,
  Focus,
  Maximize2
} from 'lucide-react';
import { analyzePrescriptionWithAI } from '../services/aiProviderManager';
import '../styles/PrescriptionUpload.css';

interface UploadProps {
  onUploadSuccess: (imageBase64: string) => void;
}

export const PrescriptionUpload: React.FC<UploadProps> = ({
  onUploadSuccess,
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyzeLockRef = useRef(false);
  const [cameraActive, setCameraActive] = useState(false);
  const navigate = useNavigate();

  // Set up camera stream when it becomes available
  useEffect(() => {
    if (cameraStream && videoRef.current && cameraActive) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play().then(() => {
          setCameraLoading(false);
        }).catch(err => {
          console.error('Error playing video:', err);
          setCameraLoading(false);
        });
      };
    }
  }, [cameraStream, cameraActive]);

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result as string;
      setPreview(result);
    };

    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleCameraCapture = async () => {
    setCameraLoading(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
      });

      setCameraStream(stream);
      setCameraActive(true);
    } catch (error) {
      console.error('Camera error:', error);
      setCameraLoading(false);
      alert('Unable to access camera. Please check permissions or try uploading an image instead.');
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      
      // Check if video is actually playing
      if (video.readyState !== video.HAVE_ENOUGH_DATA) {
        alert('Camera is still loading. Please wait a moment.');
        return;
      }

      const context = canvasRef.current.getContext('2d');
      if (context) {
        // Set canvas size to match video
        const width = video.videoWidth;
        const height = video.videoHeight;
        
        if (width === 0 || height === 0) {
          alert('Camera not ready. Please try again.');
          return;
        }

        canvasRef.current.width = width;
        canvasRef.current.height = height;
        
        // Draw the current video frame
        context.drawImage(video, 0, 0, width, height);
        
        // Convert to base64
        const imageData = canvasRef.current.toDataURL('image/jpeg', 0.9);
        setPreview(imageData);
        setFileName('camera-capture.jpg');

        // Stop camera stream
        const stream = video.srcObject as MediaStream;
        stream.getTracks().forEach((track) => track.stop());
        setCameraActive(false);
      }
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setCameraLoading(false);
  };

  const handleAnalyze = async () => {
    if (analyzeLockRef.current || isLoading) {
      return;
    }

    if (!preview) {
      alert('Please upload an image first');
      return;
    }

    analyzeLockRef.current = true;
    setIsLoading(true);
    try {
      const result = await analyzePrescriptionWithAI(preview);
      onUploadSuccess(preview);

      if (result.analysis) {
        navigate('/analysis', { state: { analysisId: result.imageHash } });
        return;
      }

      navigate('/analysis', {
        state: {
          analysisId: result.imageHash,
          reviewMode: true,
          draftMedicines: result.draftMedicines || [],
          ocrText: result.ocrText || '',
          message: result.message,
        },
      });
    } catch (error) {
      navigate('/analysis', {
        state: {
          reviewMode: true,
          draftMedicines: [],
          ocrText: '',
          message: 'We could not read this prescription clearly. Please retake the photo or enter medicines manually.',
        },
      });
    } finally {
      analyzeLockRef.current = false;
      setIsLoading(false);
    }
  };

  const handleClearUpload = () => {
    setPreview(null);
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const tips = [
    { icon: Sun, text: 'Use good lighting' },
    { icon: Focus, text: 'Keep image in focus' },
    { icon: Maximize2, text: 'Show full prescription' },
  ];

  return (
    <div className="prescription-upload">
      <div className="upload-wrapper">
        <motion.div 
          className="upload-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="upload-header">
            <div className="header-icon">
              <Upload size={24} />
            </div>
            <div className="header-text">
              <h1>Upload Prescription</h1>
              <p>Upload or capture your prescription for instant AI analysis</p>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {!preview ? (
              <motion.div
                key="upload"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="upload-content"
              >
                {!cameraActive ? (
                  <>
                    {/* Drop Zone */}
                    <motion.div
                      className={`drop-zone ${isDragging ? 'dragging' : ''}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="drop-zone-content">
                        <motion.div 
                          className="drop-icon"
                          animate={{ y: [0, -8, 0] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <Image size={48} strokeWidth={1.5} />
                        </motion.div>
                        <h3>Drop your prescription here</h3>
                        <p>or click to browse files</p>
                        <div className="supported-formats">
                          <span>Supports: JPG, PNG, HEIC</span>
                        </div>
                      </div>
                      <div className="drop-zone-border" />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileInputChange}
                        style={{ display: 'none' }}
                      />
                    </motion.div>

                    <div className="divider">
                      <span>or</span>
                    </div>

                    {/* Camera Button */}
                    <motion.button
                      className="camera-btn"
                      onClick={handleCameraCapture}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Camera size={22} />
                      <span>Take Photo with Camera</span>
                    </motion.button>

                  </>
                ) : (
                  /* Camera View */
                  <motion.div 
                    className="camera-container"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    {cameraLoading && (
                      <div className="camera-loading">
                        <div className="spinner"></div>
                        <p>Starting camera...</p>
                      </div>
                    )}
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="camera-feed"
                      style={{ display: cameraLoading ? 'none' : 'block' }}
                    />
                    <canvas
                      ref={canvasRef}
                      style={{ display: 'none' }}
                    />
                    <div className="camera-overlay">
                      <div className="camera-frame">
                        <div className="frame-corner tl" />
                        <div className="frame-corner tr" />
                        <div className="frame-corner bl" />
                        <div className="frame-corner br" />
                        <p className="frame-hint">Position prescription within frame</p>
                      </div>
                    </div>
                    <div className="camera-controls">
                      <button className="camera-cancel-btn" onClick={stopCamera}>
                        <X size={20} />
                        <span>Cancel</span>
                      </button>
                      <button className="capture-btn" onClick={capturePhoto}>
                        <div className="capture-btn-inner" />
                      </button>
                      <div className="camera-spacer" />
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ) : (
              /* Preview Section */
              <motion.div
                key="preview"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="preview-content"
              >
                <div className="preview-header">
                  <div className="preview-status">
                    <CheckCircle size={18} />
                    <span>Image ready for analysis</span>
                  </div>
                  <button className="clear-btn" onClick={handleClearUpload}>
                    <X size={18} />
                  </button>
                </div>

                <div className="preview-image-container">
                  <img src={preview} alt="Prescription preview" />
                </div>

                <div className="preview-info">
                  <FileImage size={16} />
                  <span>{fileName}</span>
                </div>

                <div className="preview-actions">
                  <motion.button
                    className="analyze-btn"
                    onClick={handleAnalyze}
                    disabled={isLoading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isLoading ? (
                      <>
                        <div className="spinner spinner-sm" />
                        <span>Analyzing...</span>
                      </>
                    ) : (
                      <>
                        <FileImage size={20} />
                        <span>Analyze Prescription</span>
                      </>
                    )}
                  </motion.button>

                  {fileName.includes('camera-capture') && (
                    <motion.button
                      className="retake-btn"
                      onClick={() => {
                        handleClearUpload();
                        handleCameraCapture();
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Camera size={20} />
                      <span>Retake Photo</span>
                    </motion.button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Tips Section */}
        <motion.div 
          className="tips-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="tips-header">
            <Lightbulb size={18} />
            <h3>Tips for Best Results</h3>
          </div>
          <div className="tips-grid">
            {tips.map((tip, index) => {
              const Icon = tip.icon;
              return (
                <div key={index} className="tip-item">
                  <div className="tip-icon">
                    <Icon size={18} />
                  </div>
                  <span>{tip.text}</span>
                </div>
              );
            })}
          </div>
          <div className="tips-note">
            <AlertCircle size={14} />
            <span>Both handwritten and printed prescriptions are supported</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
